import { useState, useEffect } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { motion } from 'motion/react';
import { openPaddleCheckout, previewPrices } from '../lib/paddle';
import { useTranslation } from 'react-i18next';
import { ManageSubscriptionModal } from './ManageSubscriptionModal';
import { trackEvent } from '../lib/posthog';

const monthlyPriceId = import.meta.env.VITE_PADDLE_PRICE_ID_MONTHLY as string;
const annualPriceId = import.meta.env.VITE_PADDLE_PRICE_ID_ANNUAL as string | undefined;

interface SubscriptionCardProps {
  isPro: boolean;
  proUntil: string | null;
  userId: string;
  userEmail?: string;
  paddleSubscriptionId?: string | null;
}

export function SubscriptionCard({ isPro, proUntil, userId, userEmail, paddleSubscriptionId }: SubscriptionCardProps) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<'monthly' | 'annual'>('monthly');
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    const ids = [monthlyPriceId, annualPriceId].filter(Boolean) as string[];
    if (ids.length === 0) return;
    previewPrices(ids).then(items => {
      const map: Record<string, string> = {};
      items.forEach(i => { map[i.priceId] = i.formatted; });
      setPrices(map);
    }).catch(console.error);
  }, []);

  const selectedPriceId = plan === 'annual' && annualPriceId ? annualPriceId : monthlyPriceId;

  const handleUpgrade = async () => {
    trackEvent('upgrade_clicked', { plan, priceId: selectedPriceId });
    setLoading(true);
    try {
      await openPaddleCheckout({ supabaseUserId: userId, userEmail, priceId: selectedPriceId });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (!isPro) {
    return (
      <motion.div
        whileHover={{ y: -2 }}
        className="bg-white rounded-[2.5rem] p-6 shadow-[0_8px_40px_rgba(0,0,0,0.04)] border border-stone-100 flex flex-col gap-6 transition-all duration-300"
      >
        <div className="flex justify-between items-start px-2">
          <div>
            <h3 className="text-2xl font-extrabold text-stone-900 tracking-tight">{t('subscription.free')}</h3>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs font-bold text-stone-400 uppercase tracking-widest">{t('subscription.limitedAccess')}</span>
            </div>
          </div>
          <div className="text-right">
            <span className="text-3xl font-black text-stone-900 tracking-tighter">0€</span>
            <span className="text-sm text-stone-400 font-semibold block -mt-1">{t('subscription.perMonth')}</span>
          </div>
        </div>

        {annualPriceId && (
          <div className="flex gap-2">
            <button
              onClick={() => setPlan('monthly')}
              className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${
                plan === 'monthly' ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-500'
              }`}
            >
              {t('subscription.monthlyPlan', { price: prices[monthlyPriceId] ?? '…' })}
            </button>
            <button
              onClick={() => setPlan('annual')}
              className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${
                plan === 'annual' ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-500'
              }`}
            >
              {t('subscription.annualPlan', { price: prices[annualPriceId] ?? '…' })}
            </button>
          </div>
        )}

        <div className="bg-emerald-50/80 rounded-[1.5rem] p-4 border border-emerald-100/50 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-bold text-emerald-900">{t('subscription.upgradeToPremium')}</span>
            <span className="text-xs font-semibold text-emerald-700/80 leading-snug">
              {t('subscription.upgradeDescription')}
            </span>
          </div>
        </div>

        <button
          onClick={handleUpgrade}
          disabled={loading}
          className="w-full bg-emerald-500 text-white py-4 rounded-2xl font-bold text-sm shadow-lg hover:bg-emerald-600 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('subscription.activatePremium', { price: prices[selectedPriceId] ?? '…' })}
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      whileHover={{ y: -2 }}
      className="bg-white rounded-[2.5rem] p-6 shadow-[0_8px_40px_rgba(0,0,0,0.04)] border border-stone-100 flex flex-col gap-6 transition-all duration-300"
    >
      <div className="flex justify-between items-start px-2">
        <div>
          <h3 className="text-2xl font-extrabold text-stone-900 tracking-tight">{t('subscription.premium')}</h3>
          <div className="flex items-center gap-2 mt-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest">{t('subscription.active')}</span>
          </div>
        </div>
      </div>

      <div className="bg-emerald-50/80 rounded-[1.5rem] p-4 border border-emerald-100/50 flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-bold text-emerald-900">{t('subscription.fullAccess')}</span>
          <span className="text-xs font-semibold text-emerald-700/80 leading-snug">
            {t('subscription.allFeatures')}
          </span>
        </div>
      </div>

      {proUntil && (
        <div className="bg-stone-50 rounded-[1.5rem] p-4 border border-stone-100 text-center">
          <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">
            {t('subscription.accessUntil', { date: formatDate(proUntil) })}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3 mt-2">
        <button
          onClick={() => setManageOpen(true)}
          className="w-full bg-stone-900 text-white py-4 rounded-2xl font-bold text-sm shadow-lg hover:bg-stone-800 hover:scale-[1.02] transition-all active:scale-95"
        >
          {t('subscription.manageSubscription')}
        </button>
      </div>
      {paddleSubscriptionId && (
        <ManageSubscriptionModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          subscriptionId={paddleSubscriptionId}
        />
      )}
    </motion.div>
  );
}
